import {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react"
import {
  ISidePadding,
  ISideOffsets,
  Orientation,
  IRadius,
  Side,
  TranslateFunction,
} from "./models/Common"
import {
  componentWantsNoRaddiRendered,
  getBounds,
  IRoundedRectShape,
  isNonZero,
  isRoundedRect,
} from "./models/Rectangle"
import { Dimensions } from "./drawing/Dimensions"
import {
  FontContext,
  ScaleContext,
  getDrawingInfo,
  getInfo,
  IArea,
} from "./drawing/Common"
import { Radius } from "./drawing/Radius"
import { Id } from "./drawing/Id"
import { Glass } from "./components/Glass"
import { PrintedBorder } from "./components/PrintedBorder"
import { SolutionContext } from "./models/Form"
import PDFDocument from "pdfkit"
import SVGtoPDF from "svg-to-pdfkit"
import "./pdfkit-webpack/registerStaticFiles"
import { Box, Button } from "@mui/material"
import { InfoTable } from "./drawing/InfoTable"
import { PrintedLogo } from "./components/PrintedLogo"
import React from "react"
import MontserratFont from "./Montserrat-Regular.ttf"
import { Base64Encode } from "base64-stream"
import { BaseComponent } from "./components/Base"
import { GlassLayer } from "./components/Layers/GlassLayer"
import { GlassOrder, stringToGlassOrder } from "./models/Layers"
import { Descriptions } from "./drawing/Descriptions"
import { ISolution } from "./models/Solution"
import { useTranslation } from "react-i18next"

interface IDrawingProps {
  admin: boolean
  renderEmpty: boolean
  width: number
  glassOrder: GlassOrder
  solutionFinal?: ISolution
  test?: boolean
}

// Define the methods exposed by Drawing
export interface DrawingHandle {
  getPDF: () => Promise<string | null>
}

const GlassDrawing = forwardRef<DrawingHandle, IDrawingProps>(function Drawing(
  { admin, renderEmpty, width, glassOrder, solutionFinal, test }: IDrawingProps,
  ref
) {
  const { t } = useTranslation()
  const tf: TranslateFunction = t
  const solutionContext = useState(useContext(SolutionContext))[0]
  const solution = solutionFinal === undefined ? solutionContext : solutionFinal
  const [fontBuffer, setFontBuffer] = useState<ArrayBuffer | undefined>(
    undefined
  )
  const fontSize = (9 / 1000) * width
  let canvasW = width
  let canvasH = canvasW / Math.sqrt(2) //793

  const getPDF = () => {
    return new Promise<string | null>((resolve, reject) => {
      if (
        svgRef.current === null ||
        solution.glass.height[glassOrder] === 0 ||
        solution.glass.width[glassOrder] === 0
      ) {
        console.log("SVG is null")
        resolve(null)
        //reject(new Error("SVG element is null or dimensions are zero"))
        return null
      }

      console.log("Generating PDF...")
      let base64str = ""
      const doc = new PDFDocument({ size: "A4", layout: "landscape" })
      var stream = doc.pipe(new Base64Encode())
      stream.on("data", function (chunk) {
        base64str += chunk
      })
      stream.on("end", function () {
        //console.log(base64str)
        resolve(base64str)
      })

      if (fontBuffer) {
        doc.registerFont("Montserrat", fontBuffer)
      }
      SVGtoPDF(doc, svgRef.current, 0, 0, { useCSS: true })
      doc.end()
    })
  }

  useImperativeHandle(ref, () => ({
    getPDF,
  }))

  useEffect(() => {
    const ugh = async () => {
      setFontBuffer(
        await fetch(MontserratFont).then((res) => res.arrayBuffer())
      )
    }
    ugh()
  }, [])

  const svgRef = useRef<SVGSVGElement>(null)
  const downloadRef = useRef<HTMLAnchorElement>(null)
  if (
    Number(solution.glass.height[glassOrder]) === 0 ||
    Number(solution.glass.width[glassOrder]) === 0
  )
    return <></>

  console.log(solution)

  const downloadFromDrawing = () => {
    if (!svgRef.current) {
      return
    }

    const wTmp = canvasW
    const hTmp = canvasH
    canvasW = 1122
    canvasH = canvasW / Math.sqrt(2)
    var finalString = ""

    const doc = new PDFDocument({ size: "A4", layout: "landscape" })
    var stream = doc.pipe(new Base64Encode())
    stream.on("data", function (chunk) {
      finalString += chunk
    })
    stream.on("end", function () {
      const link = document.createElement("a")
      link.href = `data:application/pdf;base64,${finalString}`
      link.download = "Technical-drawing-test-download"
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    })

    doc.registerFont("Montserrat", fontBuffer)
    SVGtoPDF(doc, svgRef.current, 0, 0, { useCSS: true })
    doc.end()

    canvasW = wTmp
    canvasH = hTmp
  }

  const frontComponents: Array<BaseComponent> = []

  const withPrinting =
    solution.printing.withPrinting === true &&
    glassOrder === stringToGlassOrder(solution.printing.onGlass)
  const withObject = solution.printing.withLogo === true && withPrinting

  const glass = new Glass(4, solution, glassOrder, "#000000")
  glass.shape.centralOffsetX = 0
  glass.shape.centralOffsetY = 0
  frontComponents.push(glass)

  const printedBorder = withPrinting
    ? new PrintedBorder(
        10,
        solution.printing.color || "RAL9005",
        glass,
        solution.printing.doublePrint,
        solution.printing.frameThickness,
        stringToGlassOrder(solution.printing.onGlass),
        t
      )
    : undefined
  if (printedBorder) {
    frontComponents.push(printedBorder)
  }

  const printedComponents: Array<BaseComponent> = []
  if (withObject) {
    solution.printing.objects
      .sort((a, b) => {
        const yDif = a.positioning.centralOffsetY - b.positioning.centralOffsetY
        if (yDif === 0)
          return a.positioning.centralOffsetX - b.positioning.centralOffsetX
        return yDif
      })
      .forEach((obj, i) => {
        frontComponents.push(
          new PrintedLogo(
            i + 22,
            obj,
            glass,
            canvasW,
            printedBorder!.labels.dimensions!.reference!,
            t
          )
        )
        printedComponents.push(frontComponents[frontComponents.length - 1])
      })
  }

  frontComponents.sort((a, b) => a.info.id - b.info.id)

  if (solution.product.backView) {
    frontComponents.forEach((c) => {
      if (
        (c.shape as IRoundedRectShape).radius !== undefined &&
        (c.shape as IRoundedRectShape).radius !== null
      ) {
        let tmp = ((c.shape as IRoundedRectShape).radius as IRadius).tl
        ;((c.shape as IRoundedRectShape).radius as IRadius).tl = (
          (c.shape as IRoundedRectShape).radius as IRadius
        ).tr
        ;((c.shape as IRoundedRectShape).radius as IRadius).tr = tmp

        tmp = ((c.shape as IRoundedRectShape).radius as IRadius).bl
        ;((c.shape as IRoundedRectShape).radius as IRadius).bl = (
          (c.shape as IRoundedRectShape).radius as IRadius
        ).br
        ;((c.shape as IRoundedRectShape).radius as IRadius).br = tmp
      }
      if (c.shape.centralOffsetX !== undefined) c.shape.centralOffsetX *= -1
    })
  }

  const componentsWDimensions = frontComponents
    .filter((c) => c.labels && c.labels.dimensions)
    .sort((a, b) => a.info.id - b.info.id)
  const measureOffsets: ISideOffsets[] = []

  const offsets = {
    t: -0.0265 * width,
    b: 0.0265 * width,
    l: -0.0265 * width,
    r: 0.0265 * width,
  } as ISidePadding
  measureOffsets.push(offsets)

  for (let c of componentsWDimensions) {
    measureOffsets.push({ ...offsets })

    if (c.labels.dimensions?.sides.includes(Side.Top)) offsets.t -= 22
    if (c.labels.dimensions?.sides.includes(Side.Bottom)) offsets.b += 22
    if (c.labels.dimensions?.sides.includes(Side.Left)) offsets.l -= 22
    if (c.labels.dimensions?.sides.includes(Side.Right)) offsets.r += 22
  }

  const drawingFrontArea = {
    width: canvasW,
    height: 0.6 * canvasH,
    offsetX: 0,
    offsetY: 20,
  } as IArea

  const drawingProfileArea = {
    width: drawingFrontArea.width,
    height: 0.16 * canvasH,
    offsetX: drawingFrontArea.offsetX,
    offsetY: drawingFrontArea.height + drawingFrontArea.offsetY,
  } as IArea

  const notesArea = {
    width: 0.3 * canvasW,
    height: canvasH - (drawingProfileArea.height + drawingProfileArea.offsetY),
    offsetX: 20,
    offsetY: drawingFrontArea.height + drawingProfileArea.height,
  } as IArea

  const infoTableArea = {
    width: 0.65 * canvasW - 20,
    height: 0.24 * canvasH - 20,
    offsetX: 0.35 * canvasW,
    offsetY: drawingFrontArea.height + drawingProfileArea.height,
  } as IArea

  const {
    scale: scaleFront,
    drawingBounds,
    componentBounds,
    drawingOrientation,
  } = getDrawingInfo(
    frontComponents,
    drawingFrontArea,
    { t: fontSize, l: fontSize, r: fontSize, b: fontSize },
    measureOffsets
  )
  const { scale: scaleProfile } = getInfo(scaleFront, drawingProfileArea, {
    t: 0,
    l: 0,
    r: 0,
    b: 0,
  })

  ///////SIDE VIEW
  const profileOffset =
    -0.5 * (componentBounds.x + componentBounds._x) -
    0.5 * scaleFront.size.invert(offsets.r + offsets.l)

  let profileGlass = new GlassLayer(
    solution.glass.coverGlass[glassOrder],
    glassOrder,
    4,
    solution,
    profileOffset,
    0,
    t,
    "#000000"
  )

  ///////
  const radiiComponents = frontComponents.filter(
    (c) =>
      isRoundedRect(c.shape) &&
      isNonZero(c.shape.radius) &&
      !componentWantsNoRaddiRendered(c.shape)
  )

  //Give all components correct id
  ;[...frontComponents, profileGlass]
    .filter((c) => c.info.description !== "")
    .sort((a, b) => a.info.id - b.info.id)
    .forEach((c, i) => (c.info.id = i + 1))

  glass.info.id = profileGlass.info.id
  //TODO remove this empty render, should not be necessary
  if (renderEmpty || (!solution.glass.withGlass && !solution.sensor.withSensor))
    return (
      <Box
        width={canvasW}
        height={canvasH}
        style={{
          border: "1px solid #dfe0f2",
          borderRadius: "35px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          color: "#3e3259",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            border: "3px solid #3e3259",
            borderRadius: "9999px",
            marginBottom: "20px",
            padding: "5px",
            height: "20px",
            width: "20px",
            alignItems: "center",
            justifyContent: "center",
            display: "flex",
            fontSize: 24,
            fontWeight: "bold",
          }}
        >
          i
        </Box>
        <Box>
          {renderEmpty
            ? `fill in all the details and you will see your proposal here`
            : `no glass or sensor to render`}
        </Box>
      </Box>
    )

  const describedComponents = [...frontComponents, profileGlass]
    .filter((c) => c!.info.description !== "")
    .sort((a, b) => a.info.id - b.info.id)

  return (
    <FontContext.Provider value={{ size: fontSize }}>
      {test && (
        <Box sx={{ p: 2, display: "flex", columnGap: 1 }}>
          <Button variant="contained" onClick={() => downloadFromDrawing()}>
            Download form drawing
          </Button>

          <a
            ref={downloadRef}
            style={{ display: "none" }}
            download="technical-drawing.pdf"
            href="/#"
          >
            {" "}
          </a>
        </Box>
      )}

      <svg
        ref={svgRef}
        width={canvasW}
        height={canvasH}
        style={{ border: "1px solid #dfe0f2", borderRadius: "35px" }}
      >
        <g id="drawing-front">
          <ScaleContext.Provider value={scaleFront}>
            <text
              x={scaleFront.posX(
                drawingOrientation === Orientation.Landscape
                  ? drawingBounds._x
                  : drawingBounds._y
              )}
              y={2 * fontSize}
              textAnchor="middle"
              fontSize={fontSize}
              fill="#000"
            >
              {solution.product.backView ? tf("back") : tf("front")}{" "}
              {tf("view")}
            </text>

            <g className="components">
              {frontComponents.map((c) => c.render())}
            </g>
            <g className="dimensions">
              {componentsWDimensions.map(
                (c, i) =>
                  c.labels.dimensions && (
                    <Dimensions
                      key={i}
                      measuredShape={c.shape}
                      measuredInfo={c.info}
                      referentialShape={glass.shape || c.shape}
                      componentBounds={componentBounds}
                      maxTickLength={15}
                      offset={measureOffsets[i]}
                      diff={c.labels.dimensions.diff}
                      sides={c.labels.dimensions.sides}
                    />
                  )
              )}
            </g>
            <g className="ids">
              {frontComponents.map(
                (c, i) =>
                  c.labels.id && (
                    <Id
                      key={i}
                      shape={c!.shape}
                      info={c!.info}
                      {...c!.labels.id}
                    />
                  )
              )}
            </g>
            <g className="radii">
              {radiiComponents.map((c, i) => (
                <Radius
                  key={i}
                  measuredShape={c!.shape}
                  measuredInfo={c!.info}
                  arrowLength={50}
                />
              ))}
            </g>
          </ScaleContext.Provider>
        </g>

        <g id="drawing-profile">
          <text
            x={scaleProfile.posX(getBounds(profileGlass.shape)._x)}
            y={drawingProfileArea.offsetY + 3 * fontSize}
            fontSize={fontSize}
            fill="#000"
          >
            {tf("side_view", {
              thickness: profileGlass.shape.height.toFixed(2),
            })}
          </text>
          <ScaleContext.Provider value={scaleProfile}>
            {profileGlass.render()}
            {[profileGlass].map((c, i) => (
              <Dimensions
                measuredShape={c!.shape}
                measuredInfo={c!.info}
                referentialShape={c!.shape}
                componentBounds={drawingBounds}
                maxTickLength={15}
                offset={{ t: 0, b: 0, l: 0, r: -(45 + i * 22) }}
                diff={true}
                sides={c!.labels.dimensions?.sides ?? []}
              />
            ))}
            {[profileGlass].map((c) => (
              <Id
                shape={c.shape}
                info={c.info}
                fromSide={c.labels.id!.fromSide}
                locationDirection={c.labels.id!.locationDirection}
                {...c.labels.id}
              />
            ))}
          </ScaleContext.Provider>
        </g>

        <FontContext.Provider value={{ size: fontSize * 1.5 }}>
          <g id="descriptions">
            <Descriptions area={notesArea} components={describedComponents} />
          </g>
        </FontContext.Provider>

        <g id="info-table">
          <InfoTable area={infoTableArea} solution={solution} t={t} />
        </g>
      </svg>
    </FontContext.Provider>
  )
})

export default GlassDrawing
